import React from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Tabs, Table, Select, DatePicker, Button, Space, Typography, Tag, notification, Statistic, Row, Col, Checkbox, Card, Alert, Modal, Descriptions } from 'antd';
import { ReloadOutlined, PrinterOutlined, LockOutlined } from '@ant-design/icons';
import moment from 'moment';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { useBooks, BooksSelector, BooksCaption } from '../../lib/books';

const money = (v) => (v === null || v === undefined ? '' : Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
/** Builds tree rows (headings with children) from the flat chart-ordered report rows, dropping empty branches. */
function toTree(rows, valueKeys) {
  const byId = new Map(rows.map((r) => [r.id, { ...r, key: r.id, children: [] }]));
  const roots = [];
  for (const n of byId.values()) {
    const p = n.parent_id ? byId.get(n.parent_id) : null;
    if (p) p.children.push(n);
    else roots.push(n);
  }
  const prune = (nodes) =>
    nodes
      .map((n) => {
        const kids = prune(n.children);
        if (n.is_heading) {
          for (const k of valueKeys) n[k] = kids.reduce((s, c) => s + Number(c[k] || 0), 0);
        }
        const nonZero = valueKeys.some((k) => Math.abs(Number(n[k] || 0)) > 0.004);
        if (!kids.length && !nonZero) return null;
        return { ...n, children: kids.length ? kids : undefined };
      })
      .filter(Boolean);
  return prune(roots);
}

function allKeys(nodes, acc = []) {
  for (const n of nodes) {
    if (n.children) {
      acc.push(n.key);
      allKeys(n.children, acc);
    }
  }
  return acc;
}

const nameCell = (r) => (r.is_heading ? <strong>{r.name}</strong> : <span><Typography.Text type="secondary" style={{ display: 'inline-block', width: 60 }}>{r.code}</Typography.Text>{r.name}</span>);

function TreeReport({ data, valueKeys, columns, totals }) {
  const tree = React.useMemo(() => toTree(data, valueKeys), [data, valueKeys]);
  return (
    <Table
      size="small"
      rowKey="key"
      pagination={false}
      dataSource={tree}
      expandable={{ defaultExpandedRowKeys: allKeys(tree), indentSize: 18 }}
      columns={[{ title: 'Account', key: 'name', render: (_, r) => nameCell(r) }, ...columns]}
      summary={() =>
        totals && (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0}><strong>Total</strong></Table.Summary.Cell>
            {totals.map((t, i) => (
              <Table.Summary.Cell key={i} index={i + 1} align="right"><strong>{t}</strong></Table.Summary.Cell>
            ))}
          </Table.Summary.Row>
        )
      }
    />
  );
}

/** Ledger reports (xlsx DL / TB / BS / P&L / Ageing) for one company and period. */
export default function LedgerReportsPage() {
  const auth = useAuth();
  const [params] = useSearchParams();
  const { ledgerId } = useBooks();
  const [accounts, setAccounts] = React.useState([]);
  const [tab, setTab] = React.useState(params.get('tab') || (params.get('account') ? 'dl' : 'tb'));
  // ?from=YYYY-MM-DD&to=YYYY-MM-DD deep-links a period (e.g. from the year-end close list)
  const [range, setRange] = React.useState([params.get('from') ? moment(params.get('from')) : moment().startOf('year'), params.get('to') ? moment(params.get('to')) : moment()]);
  const [asOf, setAsOf] = React.useState(moment());
  const [accountId, setAccountId] = React.useState(params.get('account') ? Number(params.get('account')) : null);
  const [data, setData] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  // Transaction analysis (xlsx "Tran. analysis"): Department / Branch / Project filters and the analysis matrix
  const [refs, setRefs] = React.useState({ departments: [], branches: [], projects: [] });
  const [dims, setDims] = React.useState({ department_id: null, branch_id: null, project_id: null });
  const [dimension, setDimension] = React.useState('department');
  const [plOnly, setPlOnly] = React.useState(true);
  const [includeClosing, setIncludeClosing] = React.useState(true);
  const [ageingSide, setAgeingSide] = React.useState('debtors');
  // Budget vs Actual
  const [budgets, setBudgets] = React.useState([]);
  const [budgetId, setBudgetId] = React.useState(params.get('budget') ? Number(params.get('budget')) : null);
  const [budgetMonthly, setBudgetMonthly] = React.useState(false);
  // Year-end close
  const [fyEnd, setFyEnd] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [previewing, setPreviewing] = React.useState(false);
  const canClose = auth.user.role === 'SYSTEM_ADMIN' || auth.user.role === 'COMPANY_MANAGER';

  React.useEffect(() => {
    if (!ledgerId) return;
    request.get('/accounts', { ledger_id: ledgerId }).then((r) => r.json()).then((r) => r.status && setAccounts(r.data.filter((a) => !a.is_heading)));
    const q = { ledger_id: ledgerId };
    Promise.all([request.get('/departments', q), request.get('/branches', q), request.get('/projects', q)].map((p) => p.then((r) => r.json()))).then(([d, b, p]) =>
      setRefs({ departments: d.status ? d.data : [], branches: b.status ? b.data : [], projects: p.status ? p.data : [] })
    );
    setPreview(null);
    setData({});
    request.get('/budgets', { ledger_id: ledgerId }).then((r) => r.json()).then((r) => {
      if (!r.status) return;
      setBudgets(r.data);
      setBudgetId((cur) => (cur && r.data.some((b) => b.id === cur) ? cur : (r.data.find((b) => b.status === 'APPROVED') || r.data[0] || {}).id || null));
    });
  }, [ledgerId]);

  const load = React.useCallback(() => {
    if (!ledgerId) return;
    const from = range[0].format('YYYY-MM-DD');
    const to = range[1].format('YYYY-MM-DD');
    const dq = Object.fromEntries(Object.entries(dims).filter(([, v]) => v));
    const q = { ledger_id: ledgerId };
    const calls = {
      tb: () => request.get('/ledger/trial-balance', { ...q, ...dq, from, to, include_closing: includeClosing ? '1' : '0' }),
      bs: () => request.get('/ledger/balance-sheet', { ...q, as_of: asOf.format('YYYY-MM-DD') }),
      pl: () => request.get('/ledger/profit-loss', { ...q, ...dq, from, to }),
      analysis: () => request.get('/ledger/analysis', { ...q, from, to, dimension, pl_only: plOnly ? '1' : '0' }),
      ageing: () => request.get('/ledger/ageing', { ...q, as_of: asOf.format('YYYY-MM-DD'), side: ageingSide }),
      far: () => request.get('/ledger/far', { ...q, from, to }),
      dl: () => (accountId ? request.get('/ledger/detail', { ...q, ...dq, account_id: accountId, from, to }) : null),
      ye: () => request.get('/ledger/year-end', q),
      budget: () => (budgetId ? request.get(`/budgets/${budgetId}/report`, { to: asOf.format('YYYY-MM-DD'), ...(dims.department_id ? { department_id: dims.department_id } : {}) }) : null),
    };
    const call = calls[tab]();
    if (!call) return;
    setLoading(true);
    call
      .then((r) => r.json())
      .then((r) => {
        if (!r.status) return notification.error({ message: r.message });
        setData((d) => ({ ...d, [tab]: r.data }));
        if (tab === 'ye' && !fyEnd && r.data.suggested_fy_end) setFyEnd(moment(r.data.suggested_fy_end));
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ledgerId, tab, range, asOf, accountId, dims, dimension, plOnly, includeClosing, ageingSide, budgetId]);
  React.useEffect(load, [load]);

  const runPreview = () => {
    if (!fyEnd) return;
    setPreviewing(true);
    request.get('/ledger/year-end/preview', { ledger_id: ledgerId, fy_end: fyEnd.format('YYYY-MM-DD') })
      .then((r) => r.json())
      .then((r) => (r.status ? setPreview(r.data) : notification.error({ message: r.message })))
      .finally(() => setPreviewing(false));
  };
  const postClosing = () => {
    if (!preview) return;
    Modal.confirm({
      title: `Close the year ended ${preview.fy_end}?`,
      icon: <LockOutlined />,
      content: `A closing journal transfers the ${preview.result >= 0 ? 'profit' : 'loss'} of ${money(Math.abs(preview.result))} to ${preview.retained_earnings_account.code} ${preview.retained_earnings_account.name}. It can be reversed from Transaction Posting if needed.`,
      okText: 'Post closing journal',
      onOk: () =>
        request
          .post('/ledger/year-end/close', { ledger_id: ledgerId, fy_end: preview.fy_end })
          .then((r) => r.json())
          .then((r) => {
            if (!r.status) return notification.error({ message: r.message });
            notification.success({ message: `${r.data.entry_number} posted`, description: `Year ended ${r.data.entry_date} closed.` });
            setPreview(null);
            load();
          }),
    });
  };

  const tb = data.tb;
  const bs = data.bs;
  const pl = data.pl;
  const dl = data.dl;
  const ag = data.ageing;
  const far = data.far;
  const an = data.analysis;
  const ye = data.ye;
  const usesRange = tab === 'tb' || tab === 'pl' || tab === 'dl' || tab === 'far' || tab === 'analysis';
  const usesDims = tab === 'tb' || tab === 'pl' || tab === 'dl';
  const bva = data.budget;
  const dimSelect = (key, placeholder, options) => (
    <Select allowClear showSearch optionFilterProp="label" style={{ width: 150 }} placeholder={placeholder} value={dims[key]} onChange={(v) => setDims((d) => ({ ...d, [key]: v || null }))} options={options} />
  );
  const dimOptions = {
    department_id: refs.departments.map((d) => ({ value: d.id, label: d.name })),
    branch_id: refs.branches.map((b) => ({ value: b.id, label: `${b.code} ${b.name}` })),
    project_id: refs.projects.map((p) => ({ value: p.id, label: p.name })),
  };
  const dimsActive = Object.values(dims).some(Boolean);
  const farColumns = [
    { title: 'Asset', key: 'a', render: (_, r) => (r.is_group ? <strong>{r.code} {r.name}</strong> : <span>{r.asset_code} {r.description}</span>) },
    { title: 'Year', dataIndex: 'year', width: 60 },
    { title: '%', dataIndex: 'rate', width: 60, render: (v) => (v !== undefined ? `${v}` : '') },
    { title: 'Cost op. bal.', dataIndex: 'cost_opening', align: 'right', render: money },
    { title: 'Additions', dataIndex: 'additions', align: 'right', render: money },
    { title: 'Disposals', dataIndex: 'cost_disposals', align: 'right', render: money },
    { title: 'Cost cl. bal.', dataIndex: 'cost_closing', align: 'right', render: (v) => <strong>{money(v)}</strong> },
    { title: 'Acc. dep. op.', dataIndex: 'acc_opening', align: 'right', render: money },
    { title: 'Charge', dataIndex: 'charge', align: 'right', render: (v, r) => (v === 0 && r.projected_charge > 0 ? <Typography.Text type="secondary" title="projected - not yet run">({money(r.projected_charge)})</Typography.Text> : money(v)) },
    { title: 'Disposals', dataIndex: 'acc_disposals', align: 'right', render: money },
    { title: 'Acc. dep. cl.', dataIndex: 'acc_closing', align: 'right', render: (v) => <strong>{money(v)}</strong> },
    { title: 'NBV', dataIndex: 'nbv', align: 'right', render: (v) => <strong>{money(v)}</strong> },
  ];
  const farRows = far ? far.groups.map((g) => ({ key: `g${g.account_id}`, is_group: true, code: g.code, name: g.name, ...g.totals, children: g.assets.map((a) => ({ key: `a${a.id}`, ...a })) })) : [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 16 }} className="no-print">
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Ledger Reports
          </Typography.Title>
          <div><BooksCaption /></div>
          <Typography.Text type="secondary">Detail Ledger, Trial Balance, Balance Sheet, Profit & Loss, Transaction Analysis, Fixed Asset Register, Ageing, Budget vs Actual and the year-end close from posted transactions.</Typography.Text>
        </div>
        <Space wrap>
          <BooksSelector />
          {tab !== 'ye' && (usesRange ? <DatePicker.RangePicker value={range} onChange={(v) => v && setRange(v)} allowClear={false} /> : <DatePicker value={asOf} onChange={(v) => v && setAsOf(v)} allowClear={false} />)}
          {tab === 'dl' && <Select showSearch optionFilterProp="label" style={{ width: 300 }} placeholder="Account" value={accountId} onChange={setAccountId} options={accounts.map((a) => ({ value: a.id, label: `${a.code} ${a.name}` }))} />}
          {usesDims && dimSelect('department_id', 'Department', dimOptions.department_id)}
          {usesDims && dimSelect('branch_id', 'Branch', dimOptions.branch_id)}
          {usesDims && dimSelect('project_id', 'Project', dimOptions.project_id)}
          {tab === 'tb' && <Checkbox checked={includeClosing} onChange={(e) => setIncludeClosing(e.target.checked)}>Include year-end closing</Checkbox>}
          {tab === 'analysis' && (
            <Select style={{ width: 150 }} value={dimension} onChange={setDimension} options={[{ value: 'department', label: 'By Department' }, { value: 'branch', label: 'By Branch' }, { value: 'project', label: 'By Project' }]} />
          )}
          {tab === 'analysis' && <Checkbox checked={plOnly} onChange={(e) => setPlOnly(e.target.checked)}>P&L accounts only</Checkbox>}
          {tab === 'ageing' && <Select style={{ width: 150 }} value={ageingSide} onChange={setAgeingSide} options={[{ value: 'debtors', label: 'Debtors (customers)' }, { value: 'creditors', label: 'Creditors (suppliers)' }]} />}
          {tab === 'budget' && <Select style={{ width: 220 }} placeholder="Budget" value={budgetId} onChange={setBudgetId} options={budgets.map((b) => ({ value: b.id, label: `${b.name} (${b.status.toLowerCase()})` }))} />}
          {tab === 'budget' && dimSelect('department_id', 'Department', dimOptions.department_id)}
          {tab === 'budget' && <Checkbox checked={budgetMonthly} onChange={(e) => setBudgetMonthly(e.target.checked)}>Monthly columns</Checkbox>}
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Refresh
          </Button>
          <Button icon={<PrinterOutlined />} onClick={() => window.print()}>
            Print
          </Button>
        </Space>
      </div>
      <style>{`@media print { .ant-layout-sider, .ant-layout-header, .ant-layout-footer, .no-print, .ant-tabs-nav { display: none !important; } }`}</style>

      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: 'tb',
            label: 'Trial Balance',
            children: tb && (
              <div>
                <Typography.Text type="secondary">Period {tb.from} to {tb.to}. Debit balances positive, credit balances negative.{dimsActive ? ' Filtered by analysis dimension.' : ''}{!includeClosing ? ' Pre-closing view (year-end closing journals left out).' : ''}</Typography.Text>
                <TreeReport
                  data={tb.rows}
                  valueKeys={['opening', 'debit', 'credit', 'closing']}
                  columns={[
                    { title: 'Opening balance', dataIndex: 'opening', align: 'right', width: 140, render: money },
                    { title: 'Debit', dataIndex: 'debit', align: 'right', width: 130, render: money },
                    { title: 'Credit', dataIndex: 'credit', align: 'right', width: 130, render: money },
                    { title: 'Closing balance', dataIndex: 'closing', align: 'right', width: 140, render: money },
                  ]}
                  totals={[money(tb.totals.opening), money(tb.totals.debit), money(tb.totals.credit), money(tb.totals.closing)]}
                />
              </div>
            ),
          },
          {
            key: 'bs',
            label: 'Balance Sheet',
            children: bs && (
              <div>
                <Typography.Text type="secondary">As at {bs.as_of} (financial year from {bs.fy_start}). Profit for the period, not yet transferred to reserves: <strong>{money(bs.profit_for_period)}</strong>.</Typography.Text>
                <TreeReport data={bs.rows} valueKeys={['amount']} columns={[{ title: 'Amount', dataIndex: 'amount', align: 'right', width: 160, render: money }]} />
              </div>
            ),
          },
          {
            key: 'pl',
            label: 'Profit & Loss',
            children: pl && (
              <div>
                <Row gutter={16} style={{ marginBottom: 12 }}>
                  <Col><Statistic title={`Net ${pl.net_profit >= 0 ? 'profit' : 'loss'} for ${pl.from} to ${pl.to}`} value={money(Math.abs(pl.net_profit))} valueStyle={{ color: pl.net_profit >= 0 ? '#389e0d' : '#cf1322' }} /></Col>
                </Row>
                <Typography.Text type="secondary">Income shown as negative (credit) amounts, expenses positive, as in the trial balance.{dimsActive ? ' Filtered by analysis dimension.' : ''}</Typography.Text>
                <TreeReport data={pl.rows} valueKeys={['amount']} columns={[{ title: 'Amount', dataIndex: 'amount', align: 'right', width: 160, render: money }]} />
              </div>
            ),
          },
          {
            key: 'analysis',
            label: 'Transaction Analysis',
            children: an && (
              <div>
                <Typography.Text type="secondary">
                  Movement {an.from} to {an.to} per account, split by {an.dimension}. Debits positive, credits negative; postings without a {an.dimension} fall under Unallocated. Set which accounts require a {an.dimension} on the <Link to="/chart-of-accounts">Chart of Accounts</Link>.
                </Typography.Text>
                <TreeReport
                  data={an.rows}
                  valueKeys={[...an.columns.map((c) => c.key), 'total']}
                  columns={[
                    ...an.columns.map((c) => ({ title: c.name, dataIndex: c.key, align: 'right', width: 130, render: money })),
                    { title: 'Total', dataIndex: 'total', align: 'right', width: 140, render: (v) => <strong>{money(v)}</strong> },
                  ]}
                  totals={[...an.columns.map((c) => money(an.totals[c.key])), money(an.totals.total)]}
                />
              </div>
            ),
          },
          {
            key: 'dl',
            label: 'Detail Ledger',
            children: !accountId ? (
              <Typography.Text type="secondary">Choose an account.</Typography.Text>
            ) : (
              dl && (
                <div>
                  <Typography.Text strong>
                    {dl.account.code} {dl.account.name}
                  </Typography.Text>
                  <Typography.Text type="secondary"> - opening balance {money(dl.opening)}, closing balance {money(dl.closing)}</Typography.Text>
                  <Table
                    size="small"
                    rowKey="id"
                    pagination={false}
                    dataSource={dl.rows}
                    style={{ marginTop: 8 }}
                    columns={[
                      { title: 'Tran. date', dataIndex: 'date', width: 100 },
                      { title: 'Tran. number', dataIndex: 'entry_number', width: 120 },
                      { title: 'Tran. reference', dataIndex: 'reference', render: (v) => v || '-' },
                      { title: 'Details', dataIndex: 'details', render: (v) => v || '-' },
                      { title: 'Dr amount', dataIndex: 'debit', align: 'right', width: 110, render: (v) => (v ? money(v) : '') },
                      { title: 'Cr amount', dataIndex: 'credit', align: 'right', width: 110, render: (v) => (v ? money(v) : '') },
                      { title: 'Balance', dataIndex: 'balance', align: 'right', width: 120, render: (v) => <strong>{money(v)}</strong> },
                      { title: 'Tran. code', dataIndex: 'transaction_code', width: 80, render: (v) => (v ? <Tag>{v}</Tag> : '') },
                      { title: 'VAT code', dataIndex: 'vat_code', width: 80, render: (v) => v || '' },
                      { title: 'VAT rate', dataIndex: 'vat_rate', width: 80, render: (v) => (v !== null && v !== undefined ? `${v}%` : '') },
                      { title: 'VAT amount', dataIndex: 'vat_amount', align: 'right', width: 100, render: (v) => (v ? money(v) : '') },
                      { title: 'Contra a/c', dataIndex: 'contra', render: (v) => v || '' },
                    ]}
                  />
                </div>
              )
            ),
          },
          {
            key: 'far',
            label: 'Fixed Asset Register',
            children: far && (
              <div>
                <Typography.Text type="secondary">
                  Period {far.from} to {far.to}. A charge in brackets is projected (the depreciation run for the period has not been posted yet). Manage assets under <Link to="/fixed-assets">Accounting → Fixed Assets</Link>.
                </Typography.Text>
                <Table
                  size="small"
                  rowKey="key"
                  pagination={false}
                  dataSource={farRows}
                  expandable={{ defaultExpandAllRows: true, indentSize: 16 }}
                  columns={farColumns}
                  scroll={{ x: 1300 }}
                  style={{ marginTop: 8 }}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={3}><strong>Total</strong></Table.Summary.Cell>
                      {['cost_opening', 'additions', 'cost_disposals', 'cost_closing', 'acc_opening', 'charge', 'acc_disposals', 'acc_closing', 'nbv'].map((k, i) => (
                        <Table.Summary.Cell key={k} index={i + 3} align="right"><strong>{money(far.totals[k])}</strong></Table.Summary.Cell>
                      ))}
                    </Table.Summary.Row>
                  )}
                />
              </div>
            ),
          },
          {
            key: 'ageing',
            label: 'Ageing',
            children: ag && (
              <div>
                <Typography.Text type="secondary">{ag.account ? `${ag.side === 'creditors' ? 'Creditors' : 'Debtors'} account ${ag.account.code} ${ag.account.name} by ${ag.side === 'creditors' ? 'supplier' : 'customer'} as at ${ag.as_of}, ${ag.side === 'creditors' ? 'payments' : 'receipts'} settling the oldest items first.` : `Open invoices as at ${ag.as_of}, by days past their due date.`}{ag.note ? ` ${ag.note}` : ''}</Typography.Text>
                <Table
                  size="small"
                  rowKey="client_id"
                  pagination={false}
                  dataSource={ag.rows}
                  style={{ marginTop: 8 }}
                  columns={[
                    { title: 'Code', dataIndex: 'client_code', width: 90, render: (v) => v || '-' },
                    { title: ag.side === 'creditors' ? 'Supplier' : ag.account ? 'Customer' : 'Client', dataIndex: 'client_name', render: (v, r) => (r.counterparty_id ? <Link to={`/counterparties?type=${ag.side === 'creditors' ? 'SUPPLIER' : 'CUSTOMER'}`}>{v}</Link> : v) },
                    { title: 'Debits', dataIndex: 'debits', align: 'right', render: money },
                    { title: 'Credits', dataIndex: 'credits', align: 'right', render: money },
                    { title: 'Balance', dataIndex: 'balance', align: 'right', render: (v) => <strong>{money(v)}</strong> },
                    { title: '0-30', dataIndex: 'b0_30', align: 'right', render: money },
                    { title: '31-60', dataIndex: 'b31_60', align: 'right', render: money },
                    { title: '61-90', dataIndex: 'b61_90', align: 'right', render: money },
                    { title: 'Over 90', dataIndex: 'b91', align: 'right', render: (v) => <span style={{ color: v ? '#cf1322' : undefined }}>{money(v)}</span> },
                  ]}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={2}><strong>Total</strong></Table.Summary.Cell>
                      {['debits', 'credits', 'balance', 'b0_30', 'b31_60', 'b61_90', 'b91'].map((k, i) => (
                        <Table.Summary.Cell key={k} index={i + 2} align="right"><strong>{money(ag.totals[k])}</strong></Table.Summary.Cell>
                      ))}
                    </Table.Summary.Row>
                  )}
                />
              </div>
            ),
          },
          {
            key: 'budget',
            label: 'Budget vs Actual',
            children: !budgets.length ? (
              <Typography.Text type="secondary">No budget for these books yet - create one under <Link to="/budgets">Accounting → Budgeting</Link>.</Typography.Text>
            ) : (
              bva && (
                <div>
                  <Row gutter={16} style={{ marginBottom: 12 }}>
                    <Col><Statistic title={`Budgeted result to ${bva.as_of}`} value={money(bva.totals.result.budget_ytd)} /></Col>
                    <Col><Statistic title="Actual result" value={money(bva.totals.result.actual_ytd)} valueStyle={{ color: bva.totals.result.actual_ytd >= bva.totals.result.budget_ytd ? '#389e0d' : '#cf1322' }} /></Col>
                    <Col><Statistic title="Variance" value={money(bva.totals.result.variance)} valueStyle={{ color: bva.totals.result.variance >= 0 ? '#389e0d' : '#cf1322' }} /></Col>
                    <Col><Statistic title="Full-year budget" value={money(bva.totals.result.annual_budget)} /></Col>
                  </Row>
                  <Typography.Text type="secondary">
                    {bva.budget.name} ({bva.budget.status.toLowerCase()}), {bva.ytd_months} month{bva.ytd_months === 1 ? '' : 's'} to {bva.as_of}. Income and expenses shown positive; a green variance is favourable (income above budget, expenses below).
                  </Typography.Text>
                  <TreeReport
                    data={bva.rows}
                    valueKeys={budgetMonthly ? bva.months.flatMap((m, i) => [`b${i}`, `a${i}`]) : ['budget_ytd', 'actual_ytd', 'variance', 'annual_budget', 'remaining']}
                    columns={
                      budgetMonthly
                        ? bva.months.map((m, i) => ({
                            title: m.label,
                            children: [
                              { title: 'Budget', key: `b${i}`, align: 'right', width: 90, render: (_, r) => money(r.months ? r.months[i].budget : r[`b${i}`]) },
                              { title: 'Actual', key: `a${i}`, align: 'right', width: 90, render: (_, r) => money(r.months ? r.months[i].actual : r[`a${i}`]) },
                            ],
                          }))
                        : [
                            { title: 'Budget YTD', dataIndex: 'budget_ytd', align: 'right', width: 120, render: money },
                            { title: 'Actual YTD', dataIndex: 'actual_ytd', align: 'right', width: 120, render: money },
                            { title: 'Variance', dataIndex: 'variance', align: 'right', width: 120, render: (v, r) => <span style={{ color: r.is_heading || v === 0 ? undefined : r.favourable ? '#389e0d' : '#cf1322' }}>{money(v)}</span> },
                            { title: '%', dataIndex: 'variance_pct', align: 'right', width: 70, render: (v, r) => (r.is_heading || v === null || v === undefined ? '' : `${v}%`) },
                            { title: 'Annual budget', dataIndex: 'annual_budget', align: 'right', width: 120, render: money },
                            { title: 'Remaining', dataIndex: 'remaining', align: 'right', width: 120, render: money },
                          ]
                    }
                  />
                  <Table
                    size="small"
                    pagination={false}
                    style={{ marginTop: 12, maxWidth: 820 }}
                    rowKey="k"
                    dataSource={[{ k: 'Income', ...bva.totals.income }, { k: 'Expenses', ...bva.totals.expenses }, { k: 'Result', ...bva.totals.result }]}
                    columns={[
                      { title: '', dataIndex: 'k', render: (v) => <strong>{v}</strong> },
                      { title: 'Budget YTD', dataIndex: 'budget_ytd', align: 'right', render: money },
                      { title: 'Actual YTD', dataIndex: 'actual_ytd', align: 'right', render: money },
                      { title: 'Variance', dataIndex: 'variance', align: 'right', render: money },
                      { title: 'Annual budget', dataIndex: 'annual_budget', align: 'right', render: money },
                      { title: 'Remaining', dataIndex: 'remaining', align: 'right', render: money },
                    ]}
                  />
                </div>
              )
            ),
          },
          {
            key: 'ye',
            label: 'Year-end Close',
            children: ye && (
              <Row gutter={[16, 16]}>
                <Col xs={24} xl={15}>
                  <Card size="small" title="Close a financial year">
                    <Typography.Paragraph type="secondary">
                      The closing journal clears every profit and loss account through the transfer account and carries the result to retained earnings, so the next year starts from zero and the balance sheet shows the result under equity. Post or delete draft transactions of the year first.
                    </Typography.Paragraph>
                    <Space wrap style={{ marginBottom: 12 }}>
                      <span>Year ended</span>
                      <DatePicker value={fyEnd} onChange={(v) => { setFyEnd(v); setPreview(null); }} allowClear={false} />
                      <Button onClick={runPreview} loading={previewing} disabled={!fyEnd}>Preview closing journal</Button>
                    </Space>
                    {preview && (
                      <div>
                        {preview.already_closed && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={`This year is already closed by ${preview.already_closed.entry_number}. Reverse that entry under Transaction Posting to close it again.`} />}
                        {preview.draft_entries > 0 && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={`${preview.draft_entries} draft transaction(s) dated in this year must be posted or deleted before closing.`} />}
                        {!preview.lines.length && !preview.already_closed && <Alert type="info" showIcon style={{ marginBottom: 12 }} message="The profit and loss accounts have no balance for this year - nothing to close." />}
                        <Descriptions size="small" column={{ xs: 1, md: 2 }} bordered style={{ marginBottom: 12 }} labelStyle={{ width: 150 }}>
                          <Descriptions.Item label="Financial year">{preview.fy_start} to {preview.fy_end}</Descriptions.Item>
                          <Descriptions.Item label={preview.result >= 0 ? 'Profit for the year' : 'Loss for the year'}>
                            <strong style={{ color: preview.result >= 0 ? '#389e0d' : '#cf1322' }}>{money(Math.abs(preview.result))}</strong>
                          </Descriptions.Item>
                          <Descriptions.Item label="Transfer account">{preview.transfer_account.code} {preview.transfer_account.name}</Descriptions.Item>
                          <Descriptions.Item label="Retained earnings">{preview.retained_earnings_account.code} {preview.retained_earnings_account.name}</Descriptions.Item>
                        </Descriptions>
                        <Table
                          size="small"
                          rowKey={(l, i) => i}
                          pagination={false}
                          dataSource={preview.lines}
                          columns={[
                            { title: 'Account', key: 'a', render: (_, l) => <span><Typography.Text type="secondary" style={{ display: 'inline-block', width: 50 }}>{l.code}</Typography.Text>{l.name}</span> },
                            { title: 'Details', dataIndex: 'details' },
                            { title: 'Debit', dataIndex: 'debit', align: 'right', width: 110, render: (v) => (v ? money(v) : '') },
                            { title: 'Credit', dataIndex: 'credit', align: 'right', width: 110, render: (v) => (v ? money(v) : '') },
                          ]}
                        />
                        {canClose && preview.lines.length > 0 && !preview.already_closed && preview.draft_entries === 0 && (
                          <Button type="primary" icon={<LockOutlined />} style={{ marginTop: 12 }} onClick={postClosing}>
                            Post closing journal
                          </Button>
                        )}
                      </div>
                    )}
                  </Card>
                </Col>
                <Col xs={24} xl={9}>
                  <Card size="small" title="Closing journals">
                    <Table
                      size="small"
                      rowKey="id"
                      pagination={false}
                      dataSource={ye.closings}
                      locale={{ emptyText: 'No year has been closed yet.' }}
                      columns={[
                        { title: 'Year ended', dataIndex: 'entry_date', width: 110 },
                        { title: 'Journal', dataIndex: 'entry_number', render: (v, r) => <Link to={`/journal?entry=${r.id}`}>{v}</Link> },
                        { title: 'Status', dataIndex: 'status', width: 100, render: (v) => <Tag color={v === 'POSTED' ? 'green' : v === 'REVERSED' ? 'red' : 'default'}>{v}</Tag> },
                        { title: 'Amount', dataIndex: 'total_debit', align: 'right', render: money },
                      ]}
                    />
                  </Card>
                </Col>
              </Row>
            ),
          },
        ]}
      />
    </div>
  );
}
